import { signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ClientContractBlocks,
  ClientContractFieldCatalogResponse,
  ClientContractFieldResponse,
  ClientContractGroupCompany,
} from '@interfaces/companys/contracts/responses/client-contract.response';
import { Observable, map, startWith } from 'rxjs';

/** Um campo do catálogo com o controle do formulário. */
export interface ClientContractFieldEntry {
  field: ClientContractFieldResponse;
  control: FormControl<number | null>;
}

/** Linha da lista de empresas do bloco "Grupo econômico". */
export type ClientContractGroupCompanyForm = FormGroup<{
  name: FormControl<string>;
  cnpj: FormControl<string>;
}>;

/** Grupo de campos para exibir junto ("Flat Fee por prazo", "Honorários"). */
export interface ClientContractFieldGroup {
  label: string;
  entries: ClientContractFieldEntry[];
}

/** Blocos sem nada ligado (Flat Fee não tem blocos). */
export function emptyClientContractBlocks(): ClientContractBlocks {
  return {
    economicGroup: { enabled: false, companies: [] },
    exclusivityException: { enabled: false, institution: null },
  };
}

/**
 * Valor fora do padrão do catálogo (só comparação, sem cálculo). Campo vazio vale o padrão no
 * backend, então não conta; campo sem padrão (necessidade de crédito) nunca fica fora.
 */
export function isClientContractFieldOutOfDefault(
  field: ClientContractFieldResponse,
  value: number | null | undefined
): boolean {
  if (field.defaultValue === null || field.defaultValue === undefined) return false;
  if (value === null || value === undefined || (value as unknown) === '') return false;
  return Number(value) !== Number(field.defaultValue);
}

/**
 * Formulário dos campos de um contrato de cliente (card 3643), montado a partir do catálogo do tipo
 * (`GET client-contracts/fields?type=`): um controle por campo de valor e os dois blocos opcionais
 * do GA. Usado pela modal Gerar contrato e pelo painel "Campos do contrato" do editor.
 *
 * As chaves do catálogo têm ponto (`credit.needAmount`), e o `FormGroup.get` leria o ponto como
 * caminho; por isso os controles ficam com o ponto trocado por `__` e a tela usa `entries`.
 */
export class ClientContractFieldsForm {
  readonly economicGroupEnabled = new FormControl<boolean>(false, { nonNullable: true });
  readonly groupCompanies = new FormArray<ClientContractGroupCompanyForm>([]);
  readonly exclusivityEnabled = new FormControl<boolean>(false, { nonNullable: true });
  readonly exclusivityInstitution = new FormControl<string>('', { nonNullable: true });

  /** Raiz: `fields` (refeito a cada catálogo) e `blocks`. Dirty/valid/disable valem para tudo. */
  readonly root = new FormGroup({
    fields: new FormGroup<Record<string, FormControl<number | null>>>({}),
    blocks: new FormGroup({
      economicGroupEnabled: this.economicGroupEnabled,
      groupCompanies: this.groupCompanies,
      exclusivityEnabled: this.exclusivityEnabled,
      exclusivityInstitution: this.exclusivityInstitution,
    }),
  });

  /** Muda a cada catálogo montado ou minuta aplicada, para a tela reler campos e blocos. */
  readonly revision = signal(0);

  private catalogValue: ClientContractFieldCatalogResponse = { fields: [], blocks: [] };
  private entriesValue: ClientContractFieldEntry[] = [];

  get catalog(): ClientContractFieldCatalogResponse {
    return this.catalogValue;
  }

  get entries(): ClientContractFieldEntry[] {
    return this.entriesValue;
  }

  /** Emite a cada mudança (e já na assinatura), para a tela recalcular etiquetas. */
  readonly changes$: Observable<void> = this.root.valueChanges.pipe(
    startWith(null),
    map(() => undefined)
  );

  /** Campos agrupados na ordem do catálogo. */
  groups(): ClientContractFieldGroup[] {
    const groups: ClientContractFieldGroup[] = [];
    for (const entry of this.entriesValue) {
      const label = entry.field.group ?? '';
      let group = groups.find(item => item.label === label);
      if (!group) {
        group = { label, entries: [] };
        groups.push(group);
      }
      group.entries.push(entry);
    }
    return groups;
  }

  /** O catálogo tem o bloco (só GA). */
  hasBlock(key: string): boolean {
    return this.catalogValue.blocks.some(block => block.key === key);
  }

  /**
   * Monta os controles do catálogo e aplica os valores. Sem `values`, cada campo nasce com o padrão
   * do catálogo (modal Gerar contrato); com `values`, nasce com o valor da minuta.
   */
  setCatalog(
    catalog: ClientContractFieldCatalogResponse,
    values?: Record<string, number | null> | null,
    blocks?: ClientContractBlocks | null
  ): void {
    this.catalogValue = catalog;
    const fieldsGroup = new FormGroup<Record<string, FormControl<number | null>>>({});
    this.entriesValue = catalog.fields.map(field => {
      const validators = [];
      if (field.min !== null && field.min !== undefined) validators.push(Validators.min(field.min));
      if (field.max !== null && field.max !== undefined) validators.push(Validators.max(field.max));
      const initial = values ? (values[field.key] ?? null) : field.defaultValue;
      const control = new FormControl<number | null>(initial, validators);
      fieldsGroup.addControl(controlName(field.key), control);
      return { field, control };
    });

    const wasDisabled = this.root.disabled;
    this.root.setControl('fields', fieldsGroup, { emitEvent: false });
    if (wasDisabled) fieldsGroup.disable({ emitEvent: false });
    this.applyBlocks(blocks ?? emptyClientContractBlocks());
    this.root.markAsPristine();
    this.root.updateValueAndValidity();
    this.revision.update(value => value + 1);
  }

  /** Aplica valores e blocos de uma minuta gravada, sem marcar alterado. */
  reset(values: Record<string, number | null>, blocks: ClientContractBlocks): void {
    for (const entry of this.entriesValue) {
      entry.control.setValue(values[entry.field.key] ?? null, { emitEvent: false });
    }
    this.applyBlocks(blocks);
    this.root.markAsPristine();
    this.root.updateValueAndValidity();
    // Valores trocados sem evento: a tela precisa reler a lista de empresas e as etiquetas.
    this.revision.update(value => value + 1);
  }

  /** Valores dos campos para a API (`chave -> número`); vazio vai como nulo (= padrão). */
  readFields(): Record<string, number | null> {
    const result: Record<string, number | null> = {};
    for (const entry of this.entriesValue) {
      const value = entry.control.value;
      result[entry.field.key] =
        value === null || value === undefined || (value as unknown) === '' ? null : Number(value);
    }
    return result;
  }

  /** Blocos para a API. Fora do GA, vão sempre desligados. */
  readBlocks(): ClientContractBlocks {
    const companies: ClientContractGroupCompany[] = this.groupCompanies.controls
      .map(row => ({
        name: row.controls.name.value.trim(),
        cnpj: row.controls.cnpj.value.replace(/\D/g, ''),
      }))
      .filter(company => company.name || company.cnpj);
    const institution = this.exclusivityInstitution.value.trim();
    return {
      economicGroup: {
        enabled: this.hasBlock('economicGroup') && this.economicGroupEnabled.value,
        companies,
      },
      exclusivityException: {
        enabled: this.hasBlock('exclusivityException') && this.exclusivityEnabled.value,
        institution: institution || null,
      },
    };
  }

  /** Acrescenta uma linha na lista de empresas do grupo (marca alterado). */
  addGroupCompany(company?: ClientContractGroupCompany): void {
    // Alterado antes de mexer na lista: quem escuta o `valueChanges` já lê o dirty.
    this.groupCompanies.markAsDirty();
    this.groupCompanies.push(groupCompanyRow(company));
  }

  removeGroupCompany(index: number): void {
    this.groupCompanies.markAsDirty();
    this.groupCompanies.removeAt(index);
  }

  /** Troca a lista de empresas do grupo (pré-preenchimento pelo grupo econômico da plataforma). */
  replaceGroupCompanies(companies: ClientContractGroupCompany[]): void {
    this.groupCompanies.markAsDirty();
    this.groupCompanies.clear();
    companies.forEach(company => this.groupCompanies.push(groupCompanyRow(company)));
  }

  /**
   * Campos com valor fora dos limites do catálogo, pelo rótulo ("Taxa de performance (%)"), para a
   * mensagem que diz o que corrigir.
   */
  invalidLabels(): string[] {
    return this.entriesValue.filter(entry => entry.control.invalid).map(entry => entry.field.label);
  }

  private applyBlocks(blocks: ClientContractBlocks): void {
    this.economicGroupEnabled.setValue(!!blocks.economicGroup?.enabled, { emitEvent: false });
    this.groupCompanies.clear({ emitEvent: false });
    (blocks.economicGroup?.companies ?? []).forEach(company =>
      this.groupCompanies.push(groupCompanyRow(company), { emitEvent: false })
    );
    this.exclusivityEnabled.setValue(!!blocks.exclusivityException?.enabled, { emitEvent: false });
    this.exclusivityInstitution.setValue(blocks.exclusivityException?.institution ?? '', {
      emitEvent: false,
    });
    if (this.root.disabled) this.root.disable({ emitEvent: false });
  }
}

function controlName(key: string): string {
  return key.replace(/\./g, '__');
}

function groupCompanyRow(company?: ClientContractGroupCompany): ClientContractGroupCompanyForm {
  return new FormGroup({
    name: new FormControl<string>(company?.name ?? '', { nonNullable: true }),
    cnpj: new FormControl<string>(company?.cnpj ?? '', { nonNullable: true }),
  });
}
